import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, Navigate, Route, Routes, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./SignUp.scss";
import SignUpContextProvider from "../../context/SignUpContext";
import { SubscriptionPlan } from "./SignUpApi";
import loadable from "@loadable/component";
import { useStickyState } from "../../hooks/useStickyState";
import { TrustPilotBox } from "../common/TrustPilotBox";
import { SignUpConfirmationV2 } from "./SignUpConfirmationV2";
import { isSmsVerificationDisabled } from "../../utils/env";

const SignUpCreatePasswordV2 = loadable(
  () => import(/* webpackChunkName: "SignUpCreatePasswordV2" */ "./password/SignUpCreatePasswordV2"),
  {
    resolveComponent: (components) => components.SignUpCreatePasswordV2,
  }
) as any;

const SignUpChangePaymentDateV2 = loadable(
  () => import(/* webpackChunkName: "SignUpChangePaymentDateV2" */ "./SignUpChangePaymentDateV2"),
  {
    resolveComponent: (components) => components.SignUpChangePaymentDateV2,
  }
) as any;

const SignUpPaymentV2 = loadable(() => import(/* webpackChunkName: "SignUpPaymentV2" */ "./SignUpPaymentV2"), {
  resolveComponent: (components) => components.SignUpPaymentV2,
}) as any;

const SignUpSubscriptionV2 = loadable(
  () => import(/* webpackChunkName: "SignUpSubscriptionV2" */ "./plans/SignUpSubscriptionV2"),
  {
    resolveComponent: (components) => components.SignUpSubscriptionV2,
  }
) as any;

const SignUpAboutYourDataV2 = loadable(
  () => import(/* webpackChunkName: "SignUpAboutYourDataV2" */ "./aboutyourdata/SignUpAboutYourDataV2"),
  {
    resolveComponent: (components) => components.SignUpAboutYourDataV2,
  }
) as any;

const SignUpPhoneNumber = loadable(
  () => import(/* webpackChunkName: "SignUpPhoneNumber" */ "./contact/SignUpPhoneNumber"),
  {
    resolveComponent: (components) => components.SignUpPhoneNumber,
  }
) as any;

const SignUpEmail = loadable(() => import(/* webpackChunkName: "SignUpEmail" */ "./contact/SignUpEmail"), {
  resolveComponent: (components) => components.SignUpEmail,
}) as any;

const SignUpLegalNameV2 = loadable(() => import(/* webpackChunkName: "SignUpLegalNameV2" */ "./SignUpLegalNameV2"), {
  resolveComponent: (components) => components.SignUpLegalNameV2,
}) as any;

const SignUpVerifyPhone = loadable(
  () => import(/* webpackChunkName: "SignUpVerifyPhone" */ "./contact/SignUpVerifyPhone"),
  {
    resolveComponent: (components) => components.SignUpVerifyPhone,
  }
) as any;

const LoginSignupSwitch = loadable(
  () => import(/* webpackChunkName: "LoginSignupSwitch" */ "../common/LoginSignupSwitch"),
  {
    resolveComponent: (components) => components.LoginSignupSwitch,
  }
) as any;

const SignUpReferralsV2 = loadable(
  () => import(/* webpackChunkName: "SignUpReferralsV2" */ "./referrals/SignUpReferralsV2"),
  {
    resolveComponent: (components) => components.SignUpReferralsV2,
  }
) as any;

const SignUpSchedulePayment = loadable(
  () => import(/* webpackChunkName: "SignUpSchedulePayment" */ "./schedulepayment/SignUpSchedulePayment")
) as any;

interface ISignUp {
  setHeaderClass: (headerClass: string | null) => void;
  setWrapperClass: (wrapperClass: string | null) => void;
  setBypassAuthCheck: (bypassAuthCheck: boolean) => void;
  setDisplayBackButton: (displayBackButton: boolean) => void;
}

interface IUseSignUp {
  layout: string;
  subscriptionPlan: SubscriptionPlan | undefined;
  signUpStep: number;
  setSignUpStep: (signUpStep: number) => void;
  setSubscriptionPlan: (subscriptionPlan: SubscriptionPlan | undefined) => void;
  isBeforeSubscriptionPlansPage: boolean;
}

export const SIGN_UP_V2_INDEX_ROUTE_STEP_NO = 0;
export const SIGN_UP_V2_PHONE_NUMBER_STEP_NO = 1;
export const SIGN_UP_V2_VERIFY_PHONE_NUMBER_STEP_NO = 2;
export const SIGN_UP_V2_CREATE_PASSWORD_STEP_NO = 3;
export const SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO = 4;
export const SIGN_UP_V2_LEGAL_NAME_STEP_NO = 5;
export const SIGN_UP_V2_REFERRALS_STEP_NO = 6;
export const SIGN_UP_V2_ABOUT_YOUR_DATA_STEP_NO = 7;
export const LOADING_CREDIT_FILE_STEP_NO = 999;
export const SIGN_UP_V2_PAYMENT_STEP_NO = 8;
export const SIGN_UP_V2_CHANGE_PAYMENT_DATE_STEP_NO = 9;
export const SIGN_UP_V2_CONFIRMATION_STEP_NO = 10;
export const SIGN_UP_V2_SCHEDULE_CONFIRMATION_STEP_NO = 11;

export const SIGN_UP_V2_INDEX_ROUTE_URL = "/signup";
export const SIGN_UP_V2_PHONE_NUMBER_URL = "/signup/phone";
export const SIGN_UP_V2_VERIFY_PHONE_NUMBER_URL = "/signup/smsverification";
export const SIGN_UP_V2_LEGALNAME_URL = "/signup/legal";
export const SIGN_UP_V2_SUBSCRIPTION_PLANS_URL = "/signup/subscription";
export const SIGN_UP_V2_CREATE_PASSWORD_URL = "/signup/password";
export const SIGN_UP_V2_REFERRALS_URL = "/signup/referrals";
export const SIGN_UP_V2_ABOUT_YOUR_DATA_URL = "/signup/aboutyourdata";
export const SIGN_UP_V2_PAYMENT_URL = "/signup/payment";
export const SIGN_UP_V2_CHANGE_PAYMENT_DATE_URL = "/signup/paymentdate";
export const SIGN_UP_V2_CONFIRMATION_URL = "/signup/confirmation";
export const SIGN_UP_V2_SCHEDULE_URL = "/signup/schedule";

enum SignupPaths {
  EMAIL = "email",
  PHONE_NUMBER = "phone",
  ADDRESS = "address",
  CONTACT_INFO = "contactinfo",
  PASSWORD = "password",
  REFERRALS = "referrals",
  SCHEDULE = "schedule",
  ABOUT_YOUR_DATA = "aboutyourdata",
  SUBSCRIPTION = "subscription",
  PAYMENT = "payment",
  PAYMENT_DATE = "paymentdate",
  CONFIRMATION = "confirmation",
  LEGAL = "legal",
  PHONE_VERIFICATION = "smsverification",
}

export const freeSubscriptionPlanId = -1;
export const freePlan: Readonly<SubscriptionPlan> = Object.freeze({
  subscriptionPlanId: freeSubscriptionPlanId,
  amount: 0,
  title: "Free",
  interval: "month",
  stripePlanId: "n/a",
} as SubscriptionPlan);

const useSignUp = ({ setHeaderClass, setWrapperClass, setDisplayBackButton }: any): IUseSignUp => {
  const [isBeforeSubscriptionPlansPage, setIsBeforeSubscriptionPlansPage] = useState(false);
  const [signUpStep, setSignUpStep] = useStickyState<number>(0, "signUpStep");
  const [styleClasses, setStyleClasses] = useState<any>({
    headerClass: "",
    wrapperClass: "",
    layout: "welcome-layout",
  });
  const [subscriptionPlan, setSubscriptionPlan] = useStickyState<SubscriptionPlan | undefined>(
    undefined,
    "subscriptionPlan"
  );

  useEffect(() => {
    const styleClasses: any = getClassNames();
    setStyleClasses(styleClasses);
    const { headerClass, wrapperClass } = styleClasses;
    setHeaderClass(headerClass);
    setWrapperClass(wrapperClass);
    const displayBackButton: boolean =
      signUpStep > 0 &&
      signUpStep !== SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO &&
      signUpStep !== SIGN_UP_V2_SCHEDULE_URL &&
      signUpStep !== SIGN_UP_V2_CHANGE_PAYMENT_DATE_STEP_NO;
    setDisplayBackButton(displayBackButton);
    setIsBeforeSubscriptionPlansPage(signUpStep < SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO);
  }, [signUpStep]);

  const getClassNames = (): any => {
    switch (signUpStep) {
      case SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO:
        return { headerClass: "", wrapperClass: "unauthorized-white-layout", layout: "subscriptions-layout" };
      case LOADING_CREDIT_FILE_STEP_NO:
        return { headerClass: "d-none", wrapperClass: "", layout: "welcome-layout welcome-loading-layout" };
      default:
        return { headerClass: "", wrapperClass: "", layout: "welcome-layout" };
    }
  };

  const layout: string = styleClasses?.layout || "welcome-layout";

  return {
    layout,
    subscriptionPlan,
    signUpStep,
    setSignUpStep,
    setSubscriptionPlan,
    isBeforeSubscriptionPlansPage,
  };
};

export const SignUpV2 = ({ setHeaderClass, setWrapperClass, setBypassAuthCheck, setDisplayBackButton }: ISignUp) => {
  const { layout, signUpStep, subscriptionPlan, setSignUpStep, setSubscriptionPlan } = useSignUp({
    setHeaderClass,
    setWrapperClass,
    setDisplayBackButton,
  });

  const { t } = useTranslation();
  const outletContext = (useOutletContext as any)?.() || {};
  const { displayBackButton: contextDisplayBack, backButtonRoute } = outletContext;

  const getTargetBackRouteV2 = (): string => {
    if (backButtonRoute && backButtonRoute !== "#") {
      return backButtonRoute;
    }
    switch (signUpStep) {
      case SIGN_UP_V2_PHONE_NUMBER_STEP_NO:
        return SIGN_UP_V2_INDEX_ROUTE_URL;
      case SIGN_UP_V2_CREATE_PASSWORD_STEP_NO:
        return SIGN_UP_V2_PHONE_NUMBER_URL;
      case SIGN_UP_V2_LEGAL_NAME_STEP_NO:
        return SIGN_UP_V2_SUBSCRIPTION_PLANS_URL;
      case SIGN_UP_V2_REFERRALS_STEP_NO:
        return SIGN_UP_V2_LEGALNAME_URL;
      case SIGN_UP_V2_ABOUT_YOUR_DATA_STEP_NO:
        return SIGN_UP_V2_REFERRALS_URL;
      case SIGN_UP_V2_PAYMENT_STEP_NO:
        return SIGN_UP_V2_ABOUT_YOUR_DATA_URL;
      default:
        return SIGN_UP_V2_INDEX_ROUTE_URL;
    }
  };

  const showBottomBackButton =
    contextDisplayBack ??
    (signUpStep > 0 &&
      signUpStep !== SIGN_UP_V2_SUBSCRIPTION_PLANS_STEP_NO &&
      signUpStep !== SIGN_UP_V2_SCHEDULE_CONFIRMATION_STEP_NO &&
      signUpStep !== SIGN_UP_V2_CHANGE_PAYMENT_DATE_STEP_NO);

  const renderLoginSignUpSwitch = (): JSX.Element | null => {
    if (signUpStep > SIGN_UP_V2_INDEX_ROUTE_STEP_NO) {
      return null;
    }
    return (
      /* A plain div, not a grid row: the row's `col-10 offset-1` made this
         switch narrower and off-centre against the identical one on the
         sign-in screen. `signupSwitchRow` is styled to match that one. */
      <div className="signupSwitchRow">
        <LoginSignupSwitch isSignup={true} />
      </div>
    );
  };

  return (
    <SignUpContextProvider>
      <div className={layout}>
        <Container className="pb-3">
          {showBottomBackButton && (
            <div className="signup-standalone-back-bar">
              <Link
                to={getTargetBackRouteV2()}
                className="stepper-back-btn"
                id="stepper-back-button"
                aria-label={t("common.back")}
              >
                <i className="bi bi-arrow-left" />
                <span>{t("common.back")}</span>
              </Link>
            </div>
          )}
          {/* Under the step, not above it: the form is what the screen is for,
              and this is where sign-in puts the same control. */}
          <Row className="justify-content-center pt-2">
            <Col>
              <Routes>
                <Route index element={<SignUpEmail setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.LEGAL} element={<SignUpLegalNameV2 setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.PHONE_NUMBER} element={<SignUpPhoneNumber setSignUpStep={setSignUpStep} />} />
                {/* With verification off, nothing navigates here — but the URL
                    is still typed, bookmarked and linked from old emails, and
                    the screen it renders asks for a code that is never sent
                    and offers a resend that would call `verify_request`. So
                    the route is closed rather than merely unused, and sends
                    the visitor back to the phone step, which is now the last
                    step before the password. */}
                <Route
                  path={SignupPaths.PHONE_VERIFICATION}
                  element={
                    isSmsVerificationDisabled() ? (
                      <Navigate to={SIGN_UP_V2_PHONE_NUMBER_URL} replace />
                    ) : (
                      <SignUpVerifyPhone setSignUpStep={setSignUpStep} />
                    )
                  }
                />
                <Route path={SignupPaths.PASSWORD} element={<SignUpCreatePasswordV2 setSignUpStep={setSignUpStep} />} />
                <Route path={SignupPaths.REFERRALS} element={<SignUpReferralsV2 setSignUpStep={setSignUpStep} />} />
                <Route
                  path={SignupPaths.SCHEDULE}
                  element={<SignUpSchedulePayment setSignUpStep={setSignUpStep} subscriptionPlan={subscriptionPlan} />}
                />
                <Route
                  path={SignupPaths.ABOUT_YOUR_DATA}
                  element={
                    <SignUpAboutYourDataV2 setSignUpStep={setSignUpStep} setBypassAuthCheck={setBypassAuthCheck} />
                  }
                />
                <Route
                  path={SignupPaths.SUBSCRIPTION}
                  element={
                    <SignUpSubscriptionV2
                      setSignUpStep={setSignUpStep}
                      setBypassAuthCheck={setBypassAuthCheck}
                      setSubscriptionPlan={setSubscriptionPlan}
                    />
                  }
                />
                <Route
                  path={SignupPaths.PAYMENT}
                  element={<SignUpPaymentV2 subscriptionPlan={subscriptionPlan!} setSignUpStep={setSignUpStep} />}
                />
                <Route
                  path={SignupPaths.PAYMENT_DATE}
                  element={<SignUpChangePaymentDateV2 setSignUpStep={setSignUpStep} signUpStep={signUpStep} />}
                />
                {/* TODO: subscriptionPlan must be in signup context */}
                <Route
                  path={SignupPaths.CONFIRMATION}
                  element={<SignUpConfirmationV2 setSignUpStep={setSignUpStep} />}
                />
              </Routes>
            </Col>
          </Row>
          {renderLoginSignUpSwitch()}
          <TrustPilotBox />
        </Container>
      </div>
    </SignUpContextProvider>
  );
};
